import type { ThemeConfig } from "../themeContract";

/**
 * TEMPORARY theme deltas.
 *
 * When a screen needs a value the active theme does not define, add it here
 * rather than hard-coding it in a component. Each entry MUST carry a note
 * saying what needed it and when.
 *
 * These survive a theme flip on purpose — that is how you notice a gap is real
 * and not a one-off. Anything that outlives two flips should be promoted into
 * every themes/<id>/themeConfig.ts and deleted from here.
 *
 * Empty is the goal state.
 */
export const overrides: Partial<ThemeConfig> = {
  // color: { ...  }   // e.g. added 2026-08-02 for the equipment BPD heatmap
};

/**
 * Tint surfaces — added 2026-07-29.
 *
 * EmptyState and ContextSwitcher shipped with six literal hex values for faint
 * panel/chip backgrounds, which the contract has no field for and which invariant
 * 2 forbids in a component. Parked here rather than hard-coded.
 *
 * The shipped literals were tuned against the CUSTOM palette; on sidebar-mui's
 * #FAFAFA canvas a #FBFCFD panel is invisible. These are re-derived from this
 * theme's own primary/caution so they read correctly here — which also means
 * they are theme-specific and will need revisiting on a flip to a custom theme.
 *
 * Promote into themeContract + all four configs, or drop. See docs/gaps.md.
 */
export interface TintTokens {
  neutralPanel: string;
  neutralChip: string;
  primaryPanel: string;
  primaryChip: string;
  cautionPanel: string;
  cautionChip: string;
  cautionBorder: string;
}

export const tints: TintTokens = {
  neutralPanel: "#FFFFFF",
  neutralChip: "rgba(0,0,0,.04)",
  primaryPanel: "rgba(0,70,128,.04)",
  primaryChip: "rgba(0,70,128,.08)",
  cautionPanel: "rgba(241,158,0,.06)",
  cautionChip: "rgba(241,158,0,.16)",
  cautionBorder: "rgba(241,158,0,.40)"
};

/**
 * Categorical chip colours — added 2026-07-29.
 *
 * The context switcher gives each client a coloured avatar. The contract has no
 * categorical scale (it is semantic: positive/caution/negative), and invariant 2
 * forbids a literal palette in a component. All six carry white text at AA.
 *
 * Promote into themeContract + all four configs, or drop. See docs/gaps.md.
 */
export const chipPalette: string[] = [
  "#004680",
  "#0B2239",
  "#2E7D32",
  "#B25000",
  "#5B3E8E",
  "#00695C"
];

export const applyOverrides = (config: ThemeConfig): ThemeConfig => ({
  ...config,
  ...overrides,
  color: { ...config.color, ...(overrides.color ?? {}) },
  radius: { ...config.radius, ...(overrides.radius ?? {}) },
  motion: { ...config.motion, ...(overrides.motion ?? {}) },
  layout: { ...config.layout, ...(overrides.layout ?? {}) },
  typography: { ...config.typography, ...(overrides.typography ?? {}) }
});
